import { ajvResolver } from "@hookform/resolvers/ajv";
import { json, redirect } from "@remix-run/node";
import {
  Link,
  Form as RemixForm,
  useActionData,
  useLoaderData,
  useSubmit,
} from "@remix-run/react";
import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import type { JSONSchemaType } from "ajv";
import "lucide-react";
import { type FieldErrors, useForm } from "react-hook-form";
import { sessionCookie } from "~/cookies.js";
import { actionWrapper, loaderWrapper } from "~/lib/utils.server.js";

import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form.js";
import { HorizontalSeparatorWithText } from "~/components/ui/horizontal-separator-with-text.js";
import { Input } from "~/components/ui/input.js";

import { ApiError } from "../../../../shared/src/lib";

const schema = Type.Object({
  email: Type.String({
    pattern: "^.+@.+..+$",
    errorMessage: {
      pattern: "Invalid email format",
    },
  }),
  password: Type.String({
    minLength: 8,
    errorMessage: {
      minLength: "Password must be at least 8 characters",
    },
  }),
});

export const loader = loaderWrapper(async ({ request, api }) => {
  const url = new URL(request.url);
  const inviteId = url.searchParams.get("inviteId");
  const invite = inviteId ? await api.getInvite(inviteId) : null;

  return json({ invite });
});

export const action = actionWrapper(async ({ api, request }) => {
  const formData = await request.formData();

  const data = Object.fromEntries(formData.entries());

  if (!Value.Check(schema, data)) {
    return json(
      {
        email: { type: "value", message: "Invalid email" },
        password: { type: "value", message: "Invalid password" },
      } satisfies FieldErrors<Static<typeof schema>>,
      { status: 422 },
    );
  }

  try {
    await api.createUser({
      email: data.email,
      password: data.password,
      type: "createUserWithEmailAndPasswordIntent",
    });

    const session = await api.createUserSessionFromEmailAndPassword(
      data.email,
      data.password,
    );

    const inviteId = formData.get("inviteId");

    const redirectTo = inviteId
      ? `/invites/${inviteId}/onboarding`
      : "/onboarding";

    return redirect(redirectTo, {
      headers: { "Set-Cookie": await sessionCookie.serialize(session.token) },
    });
  } catch (e) {
    if (e instanceof ApiError && e.httpStatus === 409) {
      return json(
        {
          email: { type: "value", message: "This email is already taken." },
        } satisfies FieldErrors<Static<typeof schema>>,
        {
          status: 409,
        },
      );
    }
  }
});

export default function SignUpRoute() {
  const { invite } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();

  const form = useForm<Static<typeof schema>>({
    resolver: ajvResolver(schema as JSONSchemaType<Static<typeof schema>>),
    defaultValues: {
      email: invite ? invite.email : "",
      password: "",
    },
    errors: actionData,
  });

  const submit = useSubmit();

  let oauthAuthorizeUrlPath = "/oauth/google";
  if (invite) {
    oauthAuthorizeUrlPath += `?inviteId=${invite.id}`;
  }

  let signInUrlPath = "/sign-in";
  if (invite) {
    signInUrlPath += `?inviteId=${invite.id}`;
  }

  return (
    <div className="grid h-[100dvh] w-full place-items-center px-5">
      <div className="flex w-full flex-col items-center rounded-md border border-tokyo-border bg-tokyo-dark-600 px-10 py-10 md:w-[584px] md:px-20">
        <h1 className="text-center text-4xl font-bold">Join Annotate</h1>
        <p className="mb-8 mt-2 text-left  text-sm">
          {invite ? (
            <YouAreInvitedToJoinWorkspaceMessage
              inviterEmail={invite.inviter.email}
            />
          ) : (
            <StandardMessage />
          )}
        </p>
        <Form {...form}>
          <RemixForm
            className="flex w-full flex-col gap-4"
            method="POST"
            onSubmit={(e) => {
              const target = e.currentTarget;
              form.handleSubmit(() => {
                submit(target);
              })(e);
            }}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="jane@example.com"
                      type="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="********" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {invite && (
              <input type="hidden" name="inviteId" value={invite.id} />
            )}
            <Button>Sign up</Button>
          </RemixForm>
        </Form>
        <HorizontalSeparatorWithText className="my-8">
          or
        </HorizontalSeparatorWithText>
        <Button asChild size="lg" variant="outline" className="w-full">
          <Link to={oauthAuthorizeUrlPath}>
            <img
              className="mr-2"
              src="/google-logo.svg"
              alt=""
              width={24}
              height={24}
            />
            Continue with Google
          </Link>
        </Button>
        <p className="mt-8 text-center text-sm">
          Already have an account?{" "}
          <Link className="font-bold underline" to={signInUrlPath}>
            Sign in now
          </Link>
        </p>
      </div>
    </div>
  );
}

interface YouAreInvitedToJoinWorkspaceMessageProps {
  inviterEmail: string;
}

function YouAreInvitedToJoinWorkspaceMessage({
  inviterEmail,
}: YouAreInvitedToJoinWorkspaceMessageProps) {
  return (
    <>
      <a href={`mailto:${inviterEmail}`} className="font-medium underline">
        {inviterEmail}
      </a>{" "}
      invited you to join their their Annotate workspace. Create an account to
      sign in to your new workspace.
    </>
  );
}

function StandardMessage() {
  return "Create a new account to start crafting beautiful walkthroughs.";
}
